/**
 * LA JOURNÉE D'UN CAMION — déménagé ici le 30/10/2026, sans rien changer.
 *
 * CE FICHIER VENAIT DE `apps/web/src/journees.ts`, lignes 203 à 606. Il n'a pas
 * été réécrit : il a été déplacé tel quel, et les essais qui le gardaient
 * passent sans modification. Seul `LigneTrajet` a été renommé `LigneDeChaine`,
 * parce que `rentabilite.ts` exporte déjà un `LigneTrajet` qui désigne tout
 * autre chose — une ligne origine → destination.
 *
 *
 * POURQUOI IL CHANGE DE CÔTÉ
 * ---------------------------------------------------------------------------
 * DÉCISION DE MOHAMED DU 30/10 : sur l'écran de pointage, « tout sur le
 * serveur ». L'écran doit afficher une MARGE, donc un chiffre d'affaires et un
 * coût — qui ne se calculent que côté serveur, avec les tarifs et les coûts de
 * ressource. Or la marge se divise par des KILOMÈTRES.
 *
 * Tant que les kilomètres étaient comptés dans le navigateur et la marge sur le
 * serveur, il y avait DEUX COMPTES DU MÊME CHIFFRE. Le jour où ils divergent,
 * l'écran affiche 180 km sous une marge calculée sur 175, et rien ne prévient.
 * C'est le défaut que le lot 112 a fermé ailleurs — « une seule source ».
 *
 * CE QUE CE DÉPLACEMENT NE CHANGE PAS, et c'était la raison du choix du 14/09 :
 * le calcul reste en TypeScript, pas en SQL. Il se corrige sans migration et
 * s'éprouve en une milliseconde. Il devient simplement PARTAGÉ — exactement ce
 * qui a été fait le 17/09 pour `kilometresParOrdre`, et avant pour le mètre
 * linéaire.
 *
 * CE QU'IL CONTREDIT, ET IL FAUT LE SAVOIR : le bandeau du 14/09 disait « l'API
 * rend les arrêts dans l'ordre et les trajets déjà mesurés ; elle ne déduit
 * rien ». Désormais elle déduit la chaîne. Signalé à Mohamed le 30/10 avant le
 * déplacement.
 */

// ===========================================================================
// LA JOURNÉE D'UN CAMION — le troisième niveau, ajouté le 14/09/2026
// ===========================================================================
// Maquette validée `docs/maquettes/validation-journees.html`, écran 3.
//
// TOUT CE QUI SUIT SE CALCULE ICI ET NON EN SQL : la chaîne
// « départ → arrêt → arrêt → fin », la nature de chaque trajet, ce qui est à
// bord, les attentes, l'amplitude. L'API rend les arrêts dans l'ordre et les
// trajets déjà mesurés ; elle ne déduit rien.
//
// POURQUOI : ces règles vont bouger — c'est le premier écran qu'on livre
// dessus. Les tenir en TypeScript les fait corriger sans migration, et les
// éprouver en une milliseconde.

/** L'en-tête, tel que `GET /journees/:jour/:vehicule` le rend. */
export interface EnTeteJournee {
  readonly jour: string;
  readonly vehicule_id: string;
  readonly immatriculation: string;
  readonly chauffeurs: readonly string[];
  readonly remorques: readonly string[];
  readonly debut_site_id: string | null;
  readonly depart_site_id: string | null;
  readonly depart_nom: string | null;
  readonly depart_repris_du: string | null;
  /** Vrai quand l'exploitation a posé le lieu de début à la main. */
  readonly depart_corrige: boolean;
  readonly veille_sans_fin: boolean;
  readonly fin_site_id: string | null;
  readonly fin_nom: string | null;
  readonly debut_le: string | null;
  readonly fin_le: string | null;
  readonly pointee_le: string | null;
  readonly cloturee_le: string | null;
  readonly consommation: string | null;
  readonly consommation_reference: string | null;
  readonly prix_litre: string | null;
}

export interface ArretDeLaJournee {
  readonly id: string;
  readonly ot_id: string;
  readonly ot_numero: string;
  readonly client_nom: string;
  readonly rang: number;
  readonly rang_jour: number | null;
  readonly type: 'chargement' | 'livraison';
  readonly site_id: string;
  readonly site_nom: string;
  readonly site_ville: string | null;
  readonly heure_debut: string | null;
  readonly heure_fin: string | null;
  readonly arrive_le: string | null;
  readonly reparti_le: string | null;
  readonly fait: boolean;
  readonly quantite: string | null;
  readonly support: string | null;
  readonly poids: number | null;
}

export interface SegmentLu {
  readonly id: string;
  readonly depuis_etape_id: string | null;
  readonly vers_etape_id: string | null;
  readonly km_reels: number | null;
  readonly km_estimes: number | null;
  /** LE PÉAGE EST SUR LA ROUTE depuis le 14/09/2026 : il se paie sur un trajet. */
  readonly peage: string | null;
  /** `null` = toute la journée. Un CHOIX, pas une absence. */
  readonly peage_ot_id: string | null;
  /**
   * CHARGÉ OU À VIDE, QUAND QUELQU'UN L'A TRANCHÉ — 30/10/2026.
   * `null` = la déduction vaut. Voir la migration `20261030200000`.
   */
  readonly en_charge?: boolean | null;
}

export interface OrdreDuJour {
  readonly id: string;
  readonly numero: string;
  readonly client_nom: string;
}

/**
 * La nature d'un trajet. ELLE NE SE STOCKE PAS — décidé le 13/09, et répété
 * dans la migration : « ce qui se déduit ne se stocke pas ».
 */
export type NatureTrajet =
  | 'approche_vide'
  | 'approche_charge'
  | 'parcours_charge'
  | 'retour_vide';

export interface LigneDeChaine {
  readonly sorte: 'trajet';
  readonly depuis_etape_id: string | null;
  readonly vers_etape_id: string | null;
  readonly depuis_nom: string;
  readonly vers_nom: string;
  /**
   * LA NATURE DÉDUITE, toujours. Elle reste rendue même quand `en_charge` la
   * contredit : l'écran doit pouvoir montrer ce que Stafa aurait dit, sans
   * quoi on ne verrait plus qu'une valeur a été forcée.
   */
  readonly nature: NatureTrajet;
  /**
   * CE QUI A ÉTÉ FORCÉ, s'il y a lieu. `null` = la déduction vaut.
   * `enChargeEffectif` ci-dessous donne la réponse finale.
   */
  readonly en_charge: boolean | null;
  readonly a_bord: number;
  readonly km_reels: number | null;
  readonly km_estimes: number | null;
  readonly minutes_route: number | null;
  readonly peage: string | null;
  readonly peage_ot_id: string | null;
}

export interface LigneArret {
  readonly sorte: 'arret';
  readonly etape: ArretDeLaJournee;
  readonly mouvement: number | null;
  readonly minutes_attente: number | null;
}

export type LigneJournee = LigneDeChaine | LigneArret;

export interface Chaine {
  readonly lignes: readonly LigneJournee[];
  /**
   * LES RELEVÉS QUI NE CORRESPONDENT PLUS À UN TRAJET DE LA CHAÎNE.
   *
   * C'est tout l'intérêt d'avoir désigné un segment par ses EXTRÉMITÉS plutôt
   * que par un rang : après un réordonnancement, un relevé dont les deux bouts
   * ne sont plus consécutifs devient caduc — et l'écran peut le DIRE, au lieu
   * de garder un kilométrage qui ne mesure plus rien. Avec un rang, il se
   * serait recollé en silence sur un autre trajet.
   */
  readonly caducs: readonly SegmentLu[];
}

/**
 * CHARGÉ OU NON, RÉPONSE FINALE — 30/10/2026.
 *
 * La nature DÉDUITE dit `parcours_charge` quand de la marchandise est à bord.
 * `en_charge`, quand il est posé, l'emporte — dans les deux sens.
 *
 * PARTAGÉ PLUTÔT QUE RECOPIÉ dans chaque appelant : trois lectures de la même
 * règle finiraient par ne plus dire la même chose, et c'est la part à vide
 * d'un camion qu'on mesure.
 */
export function enChargeEffectif(trajet: LigneDeChaine): boolean {
  return trajet.en_charge === null
    ? trajet.nature === 'parcours_charge'
    : trajet.en_charge;
}

/** Le mot affiché pour une nature de trajet. */
export function natureDite(n: NatureTrajet): string {
  switch (n) {
    case 'approche_vide':
      return 'Approche à vide';
    case 'approche_charge':
      return 'Approche en charge';
    case 'parcours_charge':
      return 'Parcours en charge';
    case 'retour_vide':
      return 'Retour à vide';
  }
}

function minutesEntre(debut: string | null, fin: string | null): number | null {
  if (debut === null || fin === null) return null;
  const d = Date.parse(debut);
  const f = Date.parse(fin);
  if (!Number.isFinite(d) || !Number.isFinite(f)) return null;
  return Math.round((f - d) / 60000);
}

/** « 1 h 25 », « 0 h 45 », « — ». */
export function dureeDite(minutes: number | null): string {
  if (minutes === null) return '—';
  const signe = minutes < 0 ? '− ' : '';
  const m = Math.abs(minutes);
  return `${signe}${String(Math.floor(m / 60))} h ${String(m % 60).padStart(2, '0')}`;
}

function quantiteDe(e: ArretDeLaJournee): number | null {
  if (e.quantite === null) return null;
  const n = Number(e.quantite);
  return Number.isFinite(n) ? n : null;
}

/**
 * LE SUPPORT DE LA JOURNÉE, s'il n'y en a qu'un.
 *
 * `null` DÈS QUE DEUX SUPPORTS SE CÔTOIENT, et ce n'est pas une paresse :
 * additionner 18 palettes et 4 bacs donnerait « 22 » d'une unité qui n'existe
 * pas. L'écran affiche alors le nombre nu — un chiffre sans unité se lit comme
 * une quantité mélangée, ce qu'il est.
 */
export function supportDuJour(etapes: readonly ArretDeLaJournee[]): string | null {
  const vus = new Set<string>();
  for (const e of etapes) {
    if (e.support !== null && e.support.trim() !== '') vus.add(e.support.trim());
  }
  return vus.size === 1 ? [...vus][0]! : null;
}

/**
 * LA CHAÎNE DE LA JOURNÉE : trajet, arrêt, trajet, arrêt… trajet.
 *
 * N ARRÊTS DONNENT N+1 TRAJETS — un avant le premier, un entre chaque paire,
 * un après le dernier. Les deux extrêmes ont une borne à `null` : le lieu de
 * départ et le lieu de fin ne sont pas des arrêts, ils n'appartiennent à aucun
 * ordre de transport.
 *
 * AUCUN TRAJET SI LA JOURNÉE N'A PAS D'ARRÊT. « Du lieu de départ au lieu de
 * fin sans aucun arrêt » n'est pas un trajet — c'est une journée qui n'existe
 * pas, et la base le refuse (`segments_pas_deux_bornes`).
 *
 * LA NATURE SE DÉDUIT DE CE QUI EST À BORD ET DE CE QUI VIENT APRÈS :
 *   - rien à bord et un arrêt devant     → approche à vide ;
 *   - rien à bord et la fin devant       → retour à vide ;
 *   - chargé et un CHARGEMENT devant     → approche en charge ;
 *   - chargé, et une livraison ou la fin → parcours en charge.
 */
export function chaineDeLaJournee(
  entete: EnTeteJournee,
  etapes: readonly ArretDeLaJournee[],
  segments: readonly SegmentLu[],
): Chaine {
  const lignes: LigneJournee[] = [];
  if (etapes.length === 0) return { lignes, caducs: [...segments] };

  const departNom = entete.depart_nom ?? 'départ inconnu';
  const finNom = entete.fin_nom ?? 'fin non renseignée';
  const nomDe = (e: ArretDeLaJournee): string => e.site_ville ?? e.site_nom;

  const employes = new Set<string>();
  const trouver = (
    depuis: string | null,
    vers: string | null,
  ): SegmentLu | undefined =>
    segments.find((s) => s.depuis_etape_id === depuis && s.vers_etape_id === vers);

  let aBord = 0;
  // L'instant d'où part le premier trajet : l'heure corrigée de début quand
  // elle existe, sinon rien — on ne remonte pas à la veille pour l'inventer.
  let partiDe: string | null = entete.debut_le;

  for (let i = 0; i < etapes.length; i += 1) {
    const arret = etapes[i]!;
    const precedent = i === 0 ? null : etapes[i - 1]!;

    const depuis = precedent?.id ?? null;
    const mesure = trouver(depuis, arret.id);
    if (mesure !== undefined) employes.add(mesure.id);

    lignes.push({
      sorte: 'trajet',
      depuis_etape_id: depuis,
      vers_etape_id: arret.id,
      depuis_nom: precedent === null ? departNom : nomDe(precedent),
      vers_nom: nomDe(arret),
      nature:
        aBord === 0
          ? 'approche_vide'
          : arret.type === 'chargement'
            ? 'approche_charge'
            : 'parcours_charge',
      en_charge: mesure?.en_charge ?? null,
      a_bord: aBord,
      km_reels: mesure?.km_reels ?? null,
      km_estimes: mesure?.km_estimes ?? null,
      minutes_route: minutesEntre(partiDe, arret.arrive_le),
      peage: mesure?.peage ?? null,
      peage_ot_id: mesure?.peage_ot_id ?? null,
    });

    const q = quantiteDe(arret);
    const mouvement = q === null ? null : arret.type === 'chargement' ? q : -q;
    if (mouvement !== null) aBord += mouvement;

    lignes.push({
      sorte: 'arret',
      etape: arret,
      mouvement,
      minutes_attente: minutesEntre(arret.arrive_le, arret.reparti_le),
    });

    partiDe = arret.reparti_le;
  }

  const dernier = etapes[etapes.length - 1]!;
  const mesureFin = trouver(dernier.id, null);
  if (mesureFin !== undefined) employes.add(mesureFin.id);
  lignes.push({
    sorte: 'trajet',
    depuis_etape_id: dernier.id,
    vers_etape_id: null,
    depuis_nom: nomDe(dernier),
    vers_nom: finNom,
    nature: aBord === 0 ? 'retour_vide' : 'parcours_charge',
    en_charge: mesureFin?.en_charge ?? null,
    a_bord: aBord,
    km_reels: mesureFin?.km_reels ?? null,
    km_estimes: mesureFin?.km_estimes ?? null,
    minutes_route: minutesEntre(partiDe, entete.fin_le),
    // CE TRAJET-CI PEUT ENFIN PORTER UN PÉAGE. Il ne mène à aucun arrêt : tant
    // que le péage vivait sur l'étape, le sien n'avait aucune colonne où se
    // poser. C'est le gain concret du déplacement du 14/09.
    peage: mesureFin?.peage ?? null,
    peage_ot_id: mesureFin?.peage_ot_id ?? null,
  });

  return { lignes, caducs: segments.filter((s) => !employes.has(s.id)) };
}

export interface TotauxJournee {
  readonly km_reels: number | null;
  readonly km_estimes: number | null;
  readonly km_en_charge: number | null;
  readonly part_a_vide: number | null;
  readonly peage: number;
  readonly minutes_attente: number | null;
  readonly minutes_amplitude: number | null;
  readonly trajets_sans_km: number;
  /**
   * CE QUI EST DÉJÀ SAISI, MÊME SI LA JOURNÉE EST INACHEVÉE — 30/10/2026.
   *
   * DÉCISION DE MOHAMED DU 30/10 : sur une journée en cours de pointage,
   * « kilomètres et coût oui, marge non ».
   *
   * `km_reels` reste `null` tant qu'un trajet manque, et c'est la règle écrite
   * au-dessus : une somme partielle présentée comme un TOTAL ferait passer une
   * journée à moitié pointée pour une journée courte. Elle ne bouge pas.
   *
   * Celui-ci est autre chose : il ne prétend pas être le total, il dit ce qui
   * est saisi. Sans lui, l'écran ne peut pas distinguer une journée vide d'une
   * journée commencée — et c'est tout ce que la refonte demande.
   *
   * TOUJOURS UN NOMBRE. `0` sur une journée sans aucun relevé, et c'est un
   * fait : rien n'a été saisi.
   */
  readonly km_saisis: number;
  /**
   * Les kilomètres saisis qui portent de la marchandise. Même règle que
   * `km_saisis` face à `km_en_charge`.
   */
  readonly km_en_charge_saisis: number;
}

/**
 * LES TOTAUX DE LA JOURNÉE.
 *
 * `km_reels` VAUT `null` TANT QU'UN SEUL TRAJET N'EST PAS RELEVÉ, et c'est la
 * règle qui compte ici : une somme partielle présentée comme un total ferait
 * passer une journée à moitié pointée pour une journée courte. Le nombre de
 * trajets manquants est rendu à part, pour que l'écran dise pourquoi.
 *
 * `km_en_charge` NE COMPTE QUE LES PARCOURS EN CHARGE — ni les approches, ni
 * les retours : c'est la part des kilomètres qui porte de la marchandise, la
 * réponse à « quelle part de mes kilomètres est payée ».
 */
export function totauxDeLaJournee(
  entete: EnTeteJournee,
  etapes: readonly ArretDeLaJournee[],
  chaine: Chaine,
): TotauxJournee {
  const trajets = chaine.lignes.filter((l): l is LigneDeChaine => l.sorte === 'trajet');
  const sansKm = trajets.filter((t) => t.km_reels === null).length;

  /**
   * LES SOMMES DE CE QUI EST SAISI, ADDITIONNÉES UNE SEULE FOIS — 30/10/2026.
   *
   * `km_reels` et `km_en_charge` n'en sont que la version EFFACÉE quand un
   * trajet manque. Deux additions séparées pour le même chiffre finiraient par
   * ne plus dire la même chose, et c'est une marge qu'on divise par elles.
   */
  const saisis = trajets.reduce((s, t) => s + (t.km_reels ?? 0), 0);
  const enChargeSaisis = trajets
    .filter((t) => enChargeEffectif(t))
    .reduce((s, t) => s + (t.km_reels ?? 0), 0);

  const reels = sansKm > 0 ? null : saisis;
  const estimes = trajets.some((t) => t.km_estimes === null)
    ? null
    : trajets.reduce((s, t) => s + (t.km_estimes ?? 0), 0);
  const enCharge = sansKm > 0 ? null : enChargeSaisis;

  // LE PÉAGE SE SOMME SUR LES TRAJETS depuis le 14/09, et non plus sur les
  // arrêts : il se paie sur une route.
  const peage = trajets.reduce((s, t) => s + (t.peage === null ? 0 : Number(t.peage)), 0);

  const attentes = chaine.lignes
    .filter((l): l is LigneArret => l.sorte === 'arret')
    .map((a) => a.minutes_attente);
  const attente = attentes.some((m) => m === null)
    ? null
    : attentes.reduce<number>((s, m) => s + (m ?? 0), 0);

  // L'AMPLITUDE PREND LES HEURES CORRIGÉES QUAND ELLES EXISTENT, sinon les
  // arrêts — c'est le sens posé sur la table : « vide » veut dire « prends les
  // arrêts », et non « donnée manquante ».
  const debut = entete.debut_le ?? etapes[0]?.arrive_le ?? null;
  const fin = entete.fin_le ?? etapes[etapes.length - 1]?.reparti_le ?? null;

  return {
    km_reels: reels,
    km_estimes: estimes,
    km_en_charge: enCharge,
    part_a_vide:
      reels === null || enCharge === null || reels === 0
        ? null
        : ((reels - enCharge) / reels) * 100,
    peage,
    minutes_attente: attente,
    minutes_amplitude: minutesEntre(debut, fin),
    trajets_sans_km: sansKm,
    km_saisis: saisis,
    km_en_charge_saisis: enChargeSaisis,
  };
}

/**
 * LE CARBURANT DE LA JOURNÉE : km × conso ÷ 100 × prix du litre.
 *
 * MÊME FORMULE QUE `imputation.ts` ligne 339, et c'est voulu : deux formules
 * pour un même chiffre divergeraient le jour où l'une des deux serait
 * corrigée. La consommation employée est celle de la journée si elle est
 * posée, sinon la référence du véhicule.
 *
 * `null` DÈS QU'UN DES TROIS MANQUE. Le kilométrage incomplet, la
 * consommation absente ou l'indice du mois non publié donnent chacun un
 * résultat faux ; l'écran dit lequel manque plutôt que d'afficher un montant.
 */
export interface Carburant {
  readonly consommation: number;
  readonly reference: number | null;
  readonly ecart: number | null;
  readonly litres: number | null;
  readonly cout: number | null;
}

export function carburantDeLaJournee(
  entete: EnTeteJournee,
  kmReels: number | null,
): Carburant | null {
  const posee = entete.consommation === null ? null : Number(entete.consommation);
  const reference =
    entete.consommation_reference === null ? null : Number(entete.consommation_reference);
  const conso = posee ?? reference;
  if (conso === null || !Number.isFinite(conso)) return null;

  const prix = entete.prix_litre === null ? null : Number(entete.prix_litre);
  const litres = kmReels === null ? null : (kmReels * conso) / 100;

  return {
    consommation: conso,
    reference,
    // L'ÉCART N'A DE SENS QUE SI LA JOURNÉE SORT DE LA RÉFÉRENCE. Sans
    // consommation posée, la journée EST à la référence : l'écart est nul, pas
    // « zéro par hasard ».
    ecart: posee === null || reference === null ? null : posee - reference,
    litres,
    cout: litres === null || prix === null || !Number.isFinite(prix) ? null : litres * prix,
  };
}
